/** Shared UI building blocks. */
import { h, van, classNames, uniqueId } from "./tb.js";
import { copyText } from "./util.js";
import { toast } from "./toast.js";

const svg = van.tags("http://www.w3.org/2000/svg");

const isOptionsArg = (v) =>
  v != null && typeof v === "object" && !Array.isArray(v) && !v.nodeType && typeof v.render !== "function";

/**
 * A card section, optionally with a title/description header.
 *
 *   Card({ title, description }, ...children)
 *   Card(...children)
 */
export function Card(opts, ...children) {
  if(!isOptionsArg(opts)) {
    if(opts !== undefined) children.unshift(opts);
    opts = {};
  }
  const { title, description, class: cls } = opts;
  const header = (title || description)
    ? h(".card__header", [
        title ? h("h2.card__title", title) : null,
        description ? h("p.card__desc", description) : null,
      ])
    : null;
  return h("section.card", { class: cls }, [header, ...children]);
}

/**
 * A labelled form control with a real <label for> wired to the control's id.
 *
 *   FormGroup({ label: "Heslo" }, (id) => h("input.input", { id, type: "password" }))
 */
export function FormGroup({ label, htmlFor, hint } = {}, control) {
  const id = htmlFor || uniqueId("f-");
  const node = typeof control === "function" ? control(id) : control;
  return h(".form-group", [
    label ? h("label.label", { for: id }, label) : null,
    node,
    hint ? h("p.form-hint", hint) : null,
  ]);
}

const BTN_VARIANTS = { primary: "btn-primary", secondary: "btn-secondary", ghost: "btn-ghost" };

/**
 * Button.  Options: variant ("primary"|"secondary"|"ghost"), size ("sm"),
 * plus any DOM props (onclick, disabled, type, …).
 *
 *   Button({ variant: "ghost", size: "sm", onclick }, "Klikni")
 */
export function Button(opts, ...children) {
  if(!isOptionsArg(opts)) {
    if(opts !== undefined) children.unshift(opts);
    opts = {};
  }
  const { variant = "primary", size, class: cls, ...props } = opts;
  const classes = classNames("btn", BTN_VARIANTS[variant], size === "sm" && "btn-sm", cls);
  return h("button", { class: classes, ...props }, ...children);
}

const BADGE_TONES = { green: "badge-green", amber: "badge-amber", red: "badge-red" };

export function Badge(tone, ...children) {
  return h("span.badge", { class: BADGE_TONES[tone] || "" }, ...children);
}

/** Image thumbnail with an empty-state placeholder; hover to see it enlarged. */
export function Thumb(url) {
  if(!url) return h(".thumb.thumb--empty", "—");
  const img = h("img.thumb", { src: url, loading: "lazy", alt: "", referrerpolicy: "no-referrer" });

  let pop = null;
  const hide = () => { pop?.remove(); pop = null; };
  const show = () => {
    hide();
    pop = h("img.thumb-zoom", { src: url, referrerpolicy: "no-referrer" });
    document.body.appendChild(pop);
    const r = img.getBoundingClientRect();
    const size = 320; // matches .thumb-zoom max box; used only for placement
    let left = r.right + 8;
    if(left + size > window.innerWidth) left = r.left - size - 8;
    let top = r.top + r.height / 2 - size / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - size - 8));
    pop.style.left = `${Math.max(8, left)}px`;
    pop.style.top = `${top}px`;
  };
  img.addEventListener("mouseenter", show);
  img.addEventListener("mouseleave", hide);
  return img;
}

/** External URL shown as a monospace, truncating link. */
export function UrlLink(url) {
  return h("a.code-url.text-sky-700.hover:underline", { href: url, target: "_blank", rel: "noopener" }, url);
}

/** Last path segment of a URL (decoded), falling back to the whole string. */
export function urlBasename(url) {
  try { return decodeURIComponent(new URL(url).pathname.split("/").pop()) || url; }
  catch { return (url.split("/").pop() || url); }
}

/** A small copy-to-clipboard glyph. */
export function CopyIcon() {
  return svg.svg(
    { viewBox: "0 0 24 24", width: "14", height: "14", fill: "none", stroke: "currentColor",
      "stroke-width": "2", "stroke-linecap": "round", "stroke-linejoin": "round", class: "copy-icon", "aria-hidden": "true" },
    svg.rect({ x: "9", y: "9", width: "13", height: "13", rx: "2" }),
    svg.path({ d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" }),
  );
}

/**
 * Shows a URL's basename as a link; clicking copies the full URL to the
 * clipboard and shows a toast. A copy icon is appended after the text.
 */
export function CopyUrlLink(url) {
  const copy = async (e) => {
    e.preventDefault();
    if(await copyText(url)) toast.success("URL zkopírována do schránky");
    else toast.error("Kopírování do schránky selhalo");
  };
  return h("a.code-url.text-sky-700.hover:underline.inline-flex.items-center.gap-1",
    { href: url, title: url, onclick: copy },
    urlBasename(url),
    CopyIcon(),
  );
}

/** A button that copies `text` and briefly confirms. */
export function CopyButton(text, label = "Kopírovat URL") {
  const copied = van.state(false);
  return Button({
    variant: "ghost",
    size: "sm",
    onclick: async () => {
      if(await copyText(text)) {
        copied.val = true;
        setTimeout(() => { copied.val = false; }, 1500);
      }
    },
  }, () => copied.val ? "Zkopírováno ✓" : label);
}

/** A simple table: `headings` is an array of header cells, `rows` an array of <tr>. */
export function Table(headings, rows) {
  return h(".table-wrap", h("table.table", [
    h("thead", h("tr", headings.map((cell) => h("th", cell)))),
    h("tbody", rows),
  ]));
}
